import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  rowLinkClasses,
  Table,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import {
  canAccess,
  formatDate,
  formatInstant,
  loadFailureMessage,
} from '@evogard/domain';
import { backSearch, useTablePage } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { MirrorStamp, RefreshOutcome, StatusBadge, useErpRefresh } from './common.js';
import { BoletosTable, ChangesCard, ServiceRecordsTable } from './history.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * Um associado, os carros, os boletos, o histórico de atendimento e as
 * alterações dele — numa página (14/10/2026).
 *
 * Era a peça que faltava: o associado aparecia só como o cartão "O dono" dentro
 * da ficha de cada carro, e quem atendia alguém com três carros abria três
 * fichas para responder uma pergunta sobre a pessoa. O endereço é o `AS-` da
 * casa, e a rota aceita também o código do ERP — é o que os cartões das outras
 * fichas carregam.
 *
 * O "Atualizar do ERP" daqui traz o associado, TODOS os carros dele em
 * qualquer situação, os produtos de cada um, os boletos desde o contrato mais
 * antigo e o histórico de atendimento inteiro.
 */
export function MemberScreen() {
  const { code } = useParams({ from: '/authenticated/erp/associados/$code' });
  /* a página de cada tabela desta ficha, na URL — o "voltar" devolve a página */
  const pages = useSearch({ from: '/authenticated/erp/associados/$code' });
  const beneficiariosPage = useTablePage('beneficiarios', pages.beneficiarios);
  const serviceRecordsPage = useTablePage('atendimentos', pages.atendimentos);
  const vehiclesPage = useTablePage('veiculos', pages.veiculos);
  const boletosPage = useTablePage('boletos', pages.boletos);
  const alteracoesPage = useTablePage('alteracoes', pages.alteracoes);
  const { data: context } = useSessionContext();
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  const queryKey = ['erp', 'associado', code] as const;
  const { mutation: refresh, follow } = useErpRefresh(queryKey, () =>
    m?.erpCode ? { entity: 'associado', id: m.erpCode } : null,
  );

  const query = useQuery({
    refetchInterval: follow,
    queryKey,
    queryFn: async () => {
      const r = await api.erp.member({ params: { code } });
      if (r.status === 404)
        throw new NotFoundError('Este associado não existe no cadastro desta base');
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o associado'));
      return r.body;
    },
  });
  const m = query.data;

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/associados', label: 'Ver os associados' }}
      />
    );
  }

  return (
    <Page measure="cheia">
      <Link
        to="/erp/associados"
        search={backSearch('/erp/associados') as never}
        className={backLinkClasses()}
      >
        Associados
      </Link>

      <PageHeader title={m?.nome ?? (query.isLoading ? 'Carregando…' : 'Associado')}>
        {m && `Associado ${m.erpCode ?? m.code} · ${m.vehicles.length} veículo(s)`}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {m && canRefresh && m.erpCode && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            icon="atualizar"
          >
            {refresh.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
          </Button>
          <span className="text-sm text-texto-suave">
            traz o associado, todos os veículos em qualquer situação, os produtos, os
            boletos e o histórico de atendimento
          </span>
        </div>
      )}

      <RefreshOutcome mutation={refresh} />

      {m && (
        <Card
          title="O associado"
          descricao={
            !m.documentRevealed ? (
              'o documento aparece mascarado — revelar exige permissão própria'
            ) : (
              <MirrorStamp at={m.syncedAt} />
            )
          }
        >
          <DataList>
            <DataRow label="Nome">{m.nome ?? <NotProvided />}</DataRow>
            <DataRow label="Documento">
              {m.documento ? (
                <span className="inline-flex items-center gap-2">
                  <span className="tabular-nums">{m.documento}</span>
                  {!m.documentRevealed && <Badge tone="neutro">mascarado</Badge>}
                </span>
              ) : (
                <NotProvided />
              )}
            </DataRow>
            <DataRow label="Tipo de pessoa">{m.tipoPessoa ?? <NotProvided />}</DataRow>
            <DataRow label="Situação">
              {m.situacao ? <StatusBadge situacao={m.situacao} /> : <NotProvided />}
            </DataRow>
            <DataRow label="E-mail">{m.email ?? <NotProvided />}</DataRow>
            <DataRow label="Telefones">
              {m.phones.length > 0 ? m.phones.join(' · ') : <NotProvided />}
            </DataRow>
            <DataRow label="Endereço">{m.address ?? <NotProvided />}</DataRow>
            <DataRow label="Código no ERP">{m.erpCode ?? <NotProvided />}</DataRow>
          </DataList>
        </Card>
      )}

      {m && (
        <Card
          title="Cadastro no ERP"
          descricao="o registro inteiro do associado no SGA — documento e habilitação mascarados sem permissão própria"
        >
          <DataList>
            <DataRow label="Sexo">{m.registry.gender ?? <NotProvided />}</DataRow>
            <DataRow label="Nascimento">
              {m.registry.birthDate ? formatDate(m.registry.birthDate) : <NotProvided />}
            </DataRow>
            <DataRow label="Mãe">{m.registry.motherName ?? <NotProvided />}</DataRow>
            <DataRow label="Pai">{m.registry.fatherName ?? <NotProvided />}</DataRow>
            <DataRow label="Filhos">{m.registry.children ?? <NotProvided />}</DataRow>
            <DataRow label="RG">
              {m.registry.rg ? (
                <span className="tabular-nums">
                  {m.registry.rg}
                  {m.registry.rgIssuer && ` · ${m.registry.rgIssuer}`}
                  {m.registry.rgIssuedOn && ` · ${formatDate(m.registry.rgIssuedOn)}`}
                </span>
              ) : (
                <NotProvided />
              )}
            </DataRow>
            <DataRow label="CNH">
              {m.registry.driverLicense ? (
                <span className="tabular-nums">
                  {m.registry.driverLicense}
                  {m.registry.driverLicenseCategory &&
                    ` · categoria ${m.registry.driverLicenseCategory}`}
                  {m.registry.driverLicenseExpiresOn &&
                    ` · vence ${formatDate(m.registry.driverLicenseExpiresOn)}`}
                </span>
              ) : (
                <NotProvided />
              )}
            </DataRow>
            <DataRow label="Cadastro · contrato">
              {m.registry.registeredOn || m.registry.contractDate ? (
                <>
                  {m.registry.registeredOn ? formatDate(m.registry.registeredOn) : '—'} ·{' '}
                  {m.registry.contractDate ? formatDate(m.registry.contractDate) : '—'}
                </>
              ) : (
                <NotProvided />
              )}
            </DataRow>
            <DataRow label="Dia de vencimento">
              {m.registry.dueDay ?? <NotProvided />}
            </DataRow>
            <DataRow label="Cobrança recorrente">
              {m.registry.recurringBillingType ?? <NotProvided />}
            </DataRow>
            <DataRow label="SPC/Serasa">
              {m.registry.creditBureau === null ? (
                <NotProvided>o ERP não disse</NotProvided>
              ) : (
                <Badge tone={m.registry.creditBureau ? 'alerta' : 'neutro'}>
                  {m.registry.creditBureau ? 'sim' : 'não'}
                </Badge>
              )}
            </DataRow>
            <DataRow label="Avisos no ERP">
              {(['email', 'sms', 'whatsapp'] as const)
                .filter((c) => m.registry.notify[c] === true)
                .map((c) => ({ email: 'e-mail', sms: 'SMS', whatsapp: 'WhatsApp' })[c])
                .join(' · ') || <NotProvided>nenhum canal marcado</NotProvided>}
            </DataRow>
            <DataRow label="Código externo">
              {m.registry.externalCode ?? <NotProvided />}
            </DataRow>
            <DataRow label="Pontos">{m.registry.points ?? <NotProvided />}</DataRow>
            <DataRow label="Regional · cooperativa · voluntário">
              {[
                m.registry.regionalCode,
                m.registry.cooperativeCode,
                m.registry.volunteerCode,
              ]
                .map((v) => v ?? '—')
                .join(' · ')}
            </DataRow>
            <DataRow label="Estado civil · profissão · classificação">
              {[
                m.registry.maritalStatusCode,
                m.registry.professionCode,
                m.registry.classificationCode,
              ]
                .map((v) => v ?? '—')
                .join(' · ')}
            </DataRow>
          </DataList>
        </Card>
      )}

      {m && m.beneficiaries.length > 0 && (
        <Card title={`Beneficiários (${m.beneficiaries.length})`} bleed>
          <Table
            {...beneficiariosPage}
            label="Beneficiários do associado"
            items={m.beneficiaries}
            rowKey={(b) => b.erpCode ?? ''}
            empty="Nenhum beneficiário."
            columns={[
              {
                title: 'Código',
                minWidth: '7rem',
                cell: (b) => b.erpCode ?? <NotProvided />,
              },
              {
                title: 'Situação',
                minWidth: '9rem',
                cell: (b) => (
                  <span className="inline-flex items-center gap-2">
                    {b.situacao ?? <NotProvided />}
                    {b.missing && <Badge tone="alerta">saiu do SGA</Badge>}
                  </span>
                ),
              },
              {
                title: 'Vencimento',
                minWidth: '7rem',
                cell: (b) => b.dueDay ?? <NotProvided />,
              },
              {
                title: 'Cobrança recorrente',
                minWidth: '10rem',
                cell: (b) => b.recurringBillingType ?? <NotProvided />,
              },
              {
                title: 'Cooperativa · voluntário',
                minWidth: '10rem',
                cell: (b) => `${b.cooperativeCode ?? '—'} · ${b.volunteerCode ?? '—'}`,
              },
            ]}
          />
        </Card>
      )}

      {m && (
        <Card
          title={`Atendimentos (${m.serviceRecords.length})`}
          descricao={
            m.serviceRecordsSyncedAt === null
              ? 'o histórico deste associado ainda não foi pedido ao ERP — o que aparece veio da esteira'
              : `histórico inteiro consultado no ERP em ${formatInstant(m.serviceRecordsSyncedAt)}`
          }
          bleed
        >
          <ServiceRecordsTable
            paging={serviceRecordsPage}
            items={m.serviceRecords}
            empty={
              m.serviceRecordsSyncedAt === null
                ? 'Nenhum atendimento ainda. Use “Atualizar do ERP” para trazer o histórico inteiro.'
                : 'O ERP não tem atendimento registrado para este associado.'
            }
          />
        </Card>
      )}

      {m && (
        <Card title={`Veículos (${m.vehicles.length})`} bleed>
          <Table
            {...vehiclesPage}
            label="Veículos do associado"
            items={m.vehicles}
            rowKey={(v) => v.code}
            empty="O cadastro não tem veículo deste associado."
            rowLink={(v) => (
              <Link
                to="/erp/veiculos/$code"
                params={{ code: v.code }}
                aria-label={`Abrir ${v.placa ?? `o veículo ${v.code}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            columns={[
              {
                title: 'Placa',
                minWidth: '7rem',
                cell: (v) => v.placa ?? <NotProvided>sem placa</NotProvided>,
              },
              {
                title: 'Veículo',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) =>
                  [v.marca, v.modelo].filter(Boolean).join(' ') || (
                    <NotProvided>não informado</NotProvided>
                  ),
              },
              {
                title: 'Situação',
                minWidth: '11rem',
                cell: (v) => <StatusBadge situacao={v.situacao} />,
              },
            ]}
          />
        </Card>
      )}

      {m && (
        <Card
          title={`Boletos (${m.boletosTotal})`}
          descricao={
            m.boletosTotal > m.boletos.length
              ? `os ${m.boletos.length} mais recentes, em ordem de vencimento — o resto está na tela de boletos`
              : 'em ordem de vencimento'
          }
          bleed
        >
          <BoletosTable
            paging={boletosPage}
            label="Boletos do associado"
            items={m.boletos}
            empty="O cadastro não tem boleto deste associado."
          />
        </Card>
      )}

      {m && (
        <ChangesCard
          changes={m.changes}
          of="no cadastro deste associado"
          paging={alteracoesPage}
        />
      )}
    </Page>
  );
}
